/**
 * Twilio Release Cooldown Config Schema
 *
 * Stored in SystemConfig under category="twilio", one row:
 *   - key="release_cooldown" → the default cooling-window length, in days, the
 *     Twilio Operator Console's Release action waits before the sweep cron
 *     actually releases a marked number on Twilio.
 *
 * System-wide (not per-org) — Twilio numbers aren't org-scoped resources at
 * the console level, they're a fleet-wide pool. An operator marking one
 * specific number for release may override this default with a custom number
 * of days for that mark only; this row is only the fallback when they don't.
 *
 * The bounds below are the single source for the validator that accepts an
 * admin edit AND the per-mark override — both must agree on what's a sane
 * cooldown, or an admin-set default could become unenterable as a per-mark
 * custom value (or vice versa).
 */

import { z } from "zod";

/** Shortest accepted cooldown — below this there's no real "notice a mistake" window. */
export const RELEASE_COOLDOWN_DAYS_MIN = 1;

/** Longest accepted cooldown — a sanity ceiling; a number sits billing but unreleased this long. */
export const RELEASE_COOLDOWN_DAYS_MAX = 30;

/** Code default: 3 days. Used when the SystemConfig row is absent or unparseable. */
export const RELEASE_COOLDOWN_DAYS_DEFAULT = 3;

/**
 * Shortest accepted stale-claim window. The sweep claims a number (RELEASING)
 * before it calls Twilio, so this must comfortably exceed one Twilio release
 * round trip — set it too low and a second sweep re-drives a claim that is
 * still in flight.
 */
export const RELEASE_STALE_CLAIM_MINUTES_MIN = 5;

/**
 * Longest accepted stale-claim window — a number whose release crashed mid-flight
 * stays invisible to the sweep for this long, so it is a ceiling on how late the
 * self-heal can be.
 */
export const RELEASE_STALE_CLAIM_MINUTES_MAX = 1440;

/** Code default: 15 minutes. Used when the SystemConfig row is absent or unparseable. */
export const RELEASE_STALE_CLAIM_MINUTES_DEFAULT = 15;

export const TwilioReleaseCooldownConfigSchema = z.object({
  /** Days between marking a number for release and the sweep cron releasing it on Twilio. */
  cooldownDays: z.number().int().min(RELEASE_COOLDOWN_DAYS_MIN).max(RELEASE_COOLDOWN_DAYS_MAX),
  /**
   * Minutes after which a RELEASING claim counts as abandoned and the sweep
   * re-drives it. A pod that dies between claiming a number and recording the
   * result leaves the row claimed forever otherwise — the same never-recovers
   * shape the claim exists to prevent, one level up.
   *
   * `.default()` rather than required: rows written before this field existed
   * carry only `cooldownDays`, and a stricter schema would make every one of
   * them unparseable — silently dropping the admin's configured cooldown back
   * to the code default.
   */
  staleClaimMinutes: z
    .number()
    .int()
    .min(RELEASE_STALE_CLAIM_MINUTES_MIN)
    .max(RELEASE_STALE_CLAIM_MINUTES_MAX)
    .default(RELEASE_STALE_CLAIM_MINUTES_DEFAULT)
});

export type TwilioReleaseCooldownConfig = z.infer<typeof TwilioReleaseCooldownConfigSchema>;
